/**
 * loadDetailsColumns.ts
 *
 * Single source of truth for the ASN Gap "Load Details" table's 23 columns
 * (key/label/numeric flag/backing API field). Shared by the on-screen table
 * (`components/LoadDetails.tsx`) and the Export to Excel flow
 * (`utils/loadDetailsExportMapper.ts` / `utils/loadDetailsWorkbookBuilder.ts`)
 * so the Excel headers and export column order always exactly match what is
 * rendered on screen — there is only one place that defines the column set.
 */

import { ASNLoadDetailRow } from '../api/asnOverviewKpiApi';

export type LoadDetailsColumnKey =
  | 'asn'
  | 'shipType'
  | 'pobol'
  | 'inbound'
  | 'conops'
  | 'shipNo'
  | 'shipName'
  | 'shipReg'
  | 'shipMkt'
  | 'shipDate'
  | 'shipPd'
  | 'shipWk'
  | 'shipYr'
  | 'recvDate'
  | 'recvPd'
  | 'recvWk'
  | 'recvYr'
  | 'recvId'
  | 'recvName'
  | 'recvReg'
  | 'recvMkt'
  | 'skus'
  | 'cases';

export interface LoadDetailsColumnDef {
  key: LoadDetailsColumnKey;
  label: string;
  numeric?: boolean;
  /** The `ASNLoadDetailRow` property this column renders — this is also the
   * exact backend `sortBy` field name sent to `POST /api/asn/load-details/list`
   * (the response contract and the column's API field are the same string,
   * per the row-to-column mapping), so no separate sort-field map is needed. */
  field: keyof ASNLoadDetailRow;
}

export const LOAD_DETAILS_COLUMNS: LoadDetailsColumnDef[] = [
  { key: 'asn', label: 'ASN Available', field: 'asnAvailable' },
  { key: 'shipType', label: 'Shipment Type', field: 'shipmentType' },
  { key: 'pobol', label: 'PO / BOL Number', field: 'poBoLNumber' },
  { key: 'inbound', label: 'Inbound Delivery Number', field: 'inboundDeliveryNumber' },
  { key: 'conops', label: 'Con Ops Number', field: 'conOpsNumber' },
  { key: 'shipNo', label: 'Shipping Location #', field: 'shippingLocationNumber' },
  { key: 'shipName', label: 'Shipping Location Name', field: 'shippingLocationName' },
  { key: 'shipReg', label: 'Shipping Location Region / Unit', field: 'shippingLocationRegion' },
  { key: 'shipMkt', label: 'Shipping Location Market / POD', field: 'shippingLocationMarket' },
  { key: 'shipDate', label: 'Shipped Date', field: 'shippedDate' },
  { key: 'shipPd', label: 'Shipped Period', field: 'shippedPeriod' },
  { key: 'shipWk', label: 'Shipped Week', field: 'shippedWeek' },
  { key: 'shipYr', label: 'Shipped Year', field: 'shippedYear' },
  { key: 'recvDate', label: 'Received Date', field: 'receivedDate' },
  { key: 'recvPd', label: 'Received Period', field: 'receivedPeriod' },
  { key: 'recvWk', label: 'Received Week', field: 'receivedWeek' },
  { key: 'recvYr', label: 'Received Year', field: 'receivedYear' },
  { key: 'recvId', label: 'Receiving Location ID', field: 'receivingLocationId' },
  { key: 'recvName', label: 'Receiving Location Name', field: 'receivingLocationName' },
  { key: 'recvReg', label: 'Receiving Region / Unit', field: 'receivingRegion' },
  { key: 'recvMkt', label: 'Receiving Market / POD', field: 'receivingMarket' },
  { key: 'skus', label: 'SKU Count', numeric: true, field: 'skuCount' },
  { key: 'cases', label: 'Cases', numeric: true, field: 'cases' },
];

const formatNumber = (value: number): string => Math.round(value).toLocaleString('en-US');

/**
 * Renders an `ASNLoadDetailRow` cell value for on-screen display. Missing/
 * unmapped values render as an empty string — never "undefined"/"null"/
 * "NaN", never guessed or substituted from another field. Reused verbatim by
 * the Export to Excel mapper so on-screen and exported values always agree.
 */
export const formatLoadDetailsCellValue = (value: unknown, numeric?: boolean): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return '';
    return numeric ? formatNumber(value) : String(value);
  }
  const str = String(value).trim();
  return str;
};
